<template>
  <Dialog modal dismissableMask position="top"
    :visible="true"
    :closable="false"
    :draggable="false"
    @update:visible="$emit('close')"
  >
    <template #header>
      <div class="calibration-header">
        <div>
          <h2>
            Calibrate {{ channel.name.value}}
          </h2>
          <div style="padding-top: 1px; font-size: smaller;">
            Current time: <span style="font-style: italic; font-family: Reddit Mono;">
              {{ formattedCurrentTimestamp }}
            </span>
          </div>
        </div>
        <Button severity="secondary" size="small" @click="onClickNextChannel()">
          Next channel
          <i class="pi pi-arrow-right"></i>
        </Button>
        <i class="cancel pi pi-times" @click="$emit('close')"></i>
      </div>
    </template>

    <table>
      <tbody>
        <tr>
          <td class="headerCell">
            Sensor model:
          </td>
          <td>
            <Dropdown style="width: 100%"
              :disabled="modelPending"
              :modelValue="selectedSensorModelEnum"
              :options="sensorModelOptions"
              :optionLabel="option => option.label"
              :optionValue="option => option.value"
              :optionDisabled="option => option.disabled"
              @update:modelValue="selectSensorModel"
            >
              <template #value v-if="selectedSensorModelEnum == null">
                Unrecognized model
              </template>
            </Dropdown>
          </td>
          <td colspan="2" style="padding-left: 50px; color: red;">
            <div>
              {{ signalTypeWarning }}
            </div>
            <div>
              {{ assignmentWarning }}
            </div>
          </td>
        </tr>
        <tr>
          <td class="headerCell">
            Signal type:
          </td>
          <td>
            <Dropdown style="width: 100%"
              :disabled="calibrationPending"
              :modelValue="shared.selectedSignal"
              :options="signalOptions"
              :optionLabel="option => option.label"
              :optionValue="option => option.value"
              @update:modelValue="selectSignal"
            ></Dropdown>
          </td>
          <td class="headerCell" style="width: 0%;">
            12-bit digital:
          </td>
          <td style="">
            {{ digital_12bits }}
          </td>
        </tr>
        <tr>
          <td class="headerCell">
            Timestamp:
          </td>
          <td :style="{ color: (timestamp == 0) ? 'red' : ''}">
            <template v-if="!calibrationPending">
              {{ formattedCalibrationTimestamp }}
            </template>
          </td>
          <td class="headerCell">
            {{ bitsPerUnitLabel }}
          </td>
          <td :style="{ color: bitsPerUnitValid ? '' : 'red' }">
            <template v-if="!calibrationPending">
              {{ bitsPerUnitString }}
            </template>
          </td>
        </tr>
      </tbody>
    </table>

    <table>
      <tbody>
        <tr>
          <td></td>
          <td>Digital</td>
          <td>
            <div style="display: flex; flex-direction: row; gap: 20px; align-items: center; justify-content: center;">
              {{ signalUnit }}
              <Checkbox style="font-size: small;" v-model="shared.usePreviousSignalValues">
                Use previous values
              </Checkbox>
            </div>
          </td>
        </tr>
        <tr v-for="(point, index) in [lowDigitalPoint, highDigitalPoint]">
          <td>
              {{ (['Low digital', 'High digital'])[index] }}:
          </td>
          <td>
            <div style="display: flex; align-items: center;">
              <input type="text" class="p-inputtext" :disabled="calibrationPending" v-model.number="point.digital">
              <Button size="small" severity="secondary" :disabled="digital_13bits == null" @click="point.digital = digital_13bits" style="margin-left: 5px;">
                Use {{ digital_13bits ?? '...' }}
              </Button>
            </div>
          </td>
          <td>
            <input type="text" class="p-inputtext" :disabled="calibrationPending" v-model.number="point.signal">
          </td>
        </tr>
        <tr>
          <td>
            Offset:
          </td>
          <td></td>
          <td>
            <input type="text" class="p-inputtext" :disabled="calibrationPending" v-model.number="offset">
          </td>
        </tr>
      </tbody>
    </table>
    <div class="form-row" style="justify-content: center; margin-top: 30px; margin-bottom: 10px;">
      <Button severity="secondary" :disabled="submitDisabled" @click="sendCalibration()">
        Submit calibration
      </Button>
    </div>
  </Dialog>
</template>

<script>
import { reactive } from "vue";
import { useEventBus } from "@vueuse/core";
import store from "./store.js";
import Checkbox from  "./Checkbox.vue";
import { padStart, roundToNearestFixed, roundDownToNearestFixed, roundUpToNearestFixed } from "./utility.js";
import { zoneDate } from "./timezones.js";
import { createChannelMap } from "./appUtility.js";

let offset1980 = 315532800;
let shared = reactive({
  usePreviousSignalValues : true,
  selectedSignal: 'resistance'
});

export default {
  components: { Checkbox },
  props: [
    "port",
    "portNumber",
    "channel",
    "channelNumber",
    "sensorModels",
    "sensorModelConfigurationMode"
  ],
  setup() {
    let eventBus = useEventBus("calibrationDialog");
    let onClickNextChannel = () => eventBus.emit("nextChannel");
    return { onClickNextChannel };
  },
  data() {
    return {
      shared,
      timer: null,
      closed: false,
      digital_12bits: null,
      digital_13bits: null,
      configuredSignal: null,
      selectedSensorModelEnum: null,
      calibrations: null,
      modelPending: null,
      offset: null,
      timestamp: null,
      lowDigitalPoint: { digital: null, signal: null },
      highDigitalPoint: { digital: null, signal: null },
      previousSubmittedSignalForLowDigital: null,
      previousSubmittedSignalForHighDigital: null
    };
  },
  created() {
    // Request the calibration, and set the selected signal to match the channel's configuration in the response.
    shared.selectedSignal = null;
    this.requestCalibration();
  },
  watch: {
    channelNumber() {
      this.requestCalibration();
    },
    "channel.sensorModelNumber.value": {
      immediate: true,
      handler() {
        this.selectedSensorModelEnum = this.channel.sensorModelNumber.value;
      }
    },
    "shared.usePreviousSignalValues": {
      handler() {
        this.setSignalValues(shared.usePreviousSignalValues);
      }
    }
  },
  unmounted() {
    this.closed = true;
    clearTimeout(this.timer);
  },
  computed: {
    session() {
      return store.sessions[0];
    },
    system() {
      return this.session.system;
    },
    selectableSensorModels() {
      if (this.sensorModelConfigurationMode == "manual") {
        return this.sensorModels;
      }
      else {
        return this.sensorModels.filter(model => model.sensorType.value == this.selectedSensorModel.sensorType.value);
      }
    },
    sensorModelOptions() {
      return this.sensorModels.map((sensorModel, index) => ({
        label: sensorModel.name.value,
        value: index,
        disabled: !this.selectableSensorModels.includes(sensorModel)
      }));
    },
    signalOptions() {
      return [
        { label: "Resistance", value: "resistance" },
        { label: "Current", value: "current" },
        { label: "Voltage", value: "voltage" }
      ]
    },
    selectedCalibration() {
      switch (shared.selectedSignal) {
        case ("resistance"): return this.calibrations?.resistance;
        case ("current"): return this.calibrations?.current;
        case ("voltage"): return this.calibrations?.volts;
      };
    },
    formattedCalibrationTimestamp() {
      if (this.timestamp == 0) return "Default calibration";
      
      // All calls to 'date.getUTCxxx()' will return the 'local' value
      // when 'new Date()' is passed a local timestamp.
      let date = new Date((this.timestamp + offset1980) * 1000);
      let year = date.getUTCFullYear();
      let month = padStart(date.getUTCMonth() + 1, 2, "0");
      let day = padStart(date.getUTCDate(), 2, "0");
      let hour = padStart(date.getUTCHours(), 2, "0");
      let minute = padStart(date.getUTCMinutes(), 2, "0");
      let second = padStart(date.getUTCSeconds(), 2, "0");

      return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
    },
    formattedCurrentTimestamp() {
      let date = zoneDate(new Date(this.system.utcTime.value * 1000), this.system.timezone.value);
      let year = date.getUTCFullYear();
      let month = padStart(date.getUTCMonth() + 1, 2, "0");
      let day = padStart(date.getUTCDate(), 2, "0");
      let hour = padStart(date.getUTCHours(), 2, "0");
      let minute = padStart(date.getUTCMinutes(), 2, "0");
      let second = padStart(date.getUTCSeconds(), 2, "0");
      return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
    },
    calibrationPending() {
      return this.calibrations == null;
    },
    signalOptions() {
      return [
        { value: "resistance", label: "Resistance" },
        { value: "current", label: "Current" },
        { value: "voltage", label: "Voltage" }
      ];
    },
    signalUnit() {
      return {
        resistance: "Ohms",
        current: "Milliamps",
        voltage: "Volts"
      }[shared.selectedSignal];
    },
    bitsPerUnitLabel() {
      switch (shared.selectedSignal) {
        case "resistance": return "Bits / ohm:";
        case "current": return "Bits / mA:";
        case "voltage": return "Bits / volt:";
        default: return "Bits / unit:";
      }
    },
    selectedSensorModel() {
      return this.sensorModels[this.selectedSensorModelEnum];
    },
    assignmentWarning() {
      let channelMap = createChannelMap(store.sessions, this.system);
      let channelMapEntry = channelMap[this.channelNumber];
      let controlsOnThisChannel = channelMapEntry.filter(control => 
        control.controlType == "ani" &&
        control.boardId == "localhost" &&
        control.portNumber == this.portNumber
      );

      let unSupportedControl = controlsOnThisChannel.find(control => control.sensorType != this.selectedSensorModel.sensorType.value);
      if (unSupportedControl) {
        let sensorType = this.system.sensorTypes.value[unSupportedControl.sensorType];
        return `This channel is assigned to a ${sensorType.name} control.`;
      }
      else {
        return null;
      }
    },
    signalTypeWarning() {
      if (this.calibrationPending)
        return null;

      let sensorModel = this.selectedSensorModel;
      if (shared.selectedSignal != sensorModel.signalType.value)
       return `This sensor model requires a '${sensorModel.signalType.value}' signal type.`;
      else
        return null;
    },
    inputIndex() {
      return this.port.analogInputs.children.indexOf(this.channel);
    },
    bitsPerUnit() {
      let result = (this.highDigitalPoint.digital - this.lowDigitalPoint.digital) / (this.highDigitalPoint.signal - this.lowDigitalPoint.signal);
      return Math.abs(result);
    },
    bitsPerUnitRange() {
      let bitsPerUnitRanges = {
        resistance: this.system.controller.bitsPerOhmRange.value,
        current: this.system.controller.bitsPerMilliAmpRange.value,
        voltage: this.system.controller.bitsPerVoltRange.value
      };
      return bitsPerUnitRanges[shared.selectedSignal];
    },
    bitsPerUnitValid() {
      if (!shared.selectedSignal)
        return false;
      else
        return this.bitsPerUnit >= this.bitsPerUnitRange[0] && this.bitsPerUnit <= this.bitsPerUnitRange[1];
    },
    bitsPerUnitString() {
      // Suppose the valid range is [10.0, 20.0].
      // This code ensures that values like 9.99 or 20.01 are not rounded into the valid range by .toFixed().
      if (this.bitsPerUnit < this.bitsPerUnitRange[0])
        return roundDownToNearestFixed(this.bitsPerUnit, 0.1);
      else if (this.bitsPerUnit > this.bitsPerUnitRange[1])
        return roundUpToNearestFixed(this.bitsPerUnit, 0.1);
      else
        return roundToNearestFixed(this.bitsPerUnit, 0.1);
    },
    submitDisabled() {
      // The .signal and .digital values will have type "string" when empty or NaN/Infinity.
      return (
        this.calibrationPending || 
        typeof this.lowDigitalPoint.signal != "number" ||
        typeof this.highDigitalPoint.signal != "number" ||
        typeof this.lowDigitalPoint.digital != "number" ||
        typeof this.highDigitalPoint.digital != "number" ||
        typeof this.offset != "number"
      );
    }
  },
  methods: {
    setCalibrations(calibrations, usePreviousSignalValues) {
      this.calibrations = calibrations;
      this.updateCalibrationCopy(usePreviousSignalValues);
    },
    selectSignal(type, usePreviousSignalValues) {
      shared.selectedSignal = type || "resistance";
      this.updateCalibrationCopy(usePreviousSignalValues);
    },
    selectSensorModel(sensorModelEnum) {
      this.selectedSensorModelEnum = parseInt(sensorModelEnum);
      
      this.modelPending = true;
      store.sessions[0].changeSettings([{
        handle: this.channel.sensorModelNumber.handle,
        value: parseInt(sensorModelEnum)
      }], () => this.modelPending = false);
    },    
    updateCalibrationCopy(usePreviousSignalValues) {
      // A 'working copy' of the selected calibration is made so that 
      //  1) The original can be restored when the user cycles through the signal type options, and 
      //  2) The original signal values can be restored when the user unchecks the 'Use previous values' checkbox.
      if (usePreviousSignalValues == null) {
        usePreviousSignalValues = shared.usePreviousSignalValues;
      }
      this.timestamp = this.selectedCalibration?.timestamp;
      this.offset = this.selectedCalibration?.offset;
      this.lowDigitalPoint = {
        digital: this.selectedCalibration?.digitalLow,
        signal: null
      };
      this.highDigitalPoint = {
        digital: this.selectedCalibration?.digitalHigh,
        signal: null
      };
      this.setSignalValues(usePreviousSignalValues);
    },
    setSignalValues(usePreviousSignalValues) {
      let calibratedSignalLow = this.selectedCalibration?.signalLow;
      let calibratedSignalHigh = this.selectedCalibration?.signalHigh;
      let previousSignalForLowDigital = numericOR(parseFloat(localStorage.getItem(`calibration.prevLowDigital_${shared.selectedSignal}`)), calibratedSignalLow);
      let previousSignalForHighDigital = numericOR(parseFloat(localStorage.getItem(`calibration.prevHighDigital_${shared.selectedSignal}`)), calibratedSignalHigh);
      this.lowDigitalPoint.signal = usePreviousSignalValues ? previousSignalForLowDigital : calibratedSignalLow;
      this.highDigitalPoint.signal = usePreviousSignalValues ? previousSignalForHighDigital : calibratedSignalHigh;
    },
    sendCalibration() {
      // Get seconds since 1980 in the current timezone.
      // Use the bbone's time (which is UTC) in case the browser time is set incorrectly.
      // TODO: Remove this and just generate the timestamp in the function that handles this command.
      let boneDate = new Date(this.system.utcTime.value * 1000);
      let zonedDate = zoneDate(boneDate, this.system.timezone.value);
      let zonedTimestamp1980 = (zonedDate.getTime() / 1000) - offset1980;
      
      localStorage.setItem(`calibration.prevLowDigital_${shared.selectedSignal}`, this.lowDigitalPoint.signal);
      localStorage.setItem(`calibration.prevHighDigital_${shared.selectedSignal}`, this.highDigitalPoint.signal);

      clearTimeout(this.timer);
      this.session.queueRequest("/cmd/sendSerialCalibration", {
        portNumber: this.portNumber,
        channelNumber: this.channelNumber,
        selectedSignal: shared.selectedSignal,
        configuredSignal: shared.selectedSignal,
        configuredSensorModel: this.selectedSensorModelEnum,
        digitalLow: this.lowDigitalPoint.digital,
        digitalHigh: this.highDigitalPoint.digital,
        signalLow: this.lowDigitalPoint.signal,
        signalHigh: this.highDigitalPoint.signal,
        signalOffset: this.offset,
        timestamp: zonedTimestamp1980
      }, (response) => {
        this.waitForCalibration(true);
      });
      this.digital_13bits = null;
      this.setCalibrations(null);
    },
    requestCalibration() {
      this.calibrations = null;
      this.lowDigitalPoint = { digital: null, signal: null };
      this.highDigitalPoint = { digital: null, signal: null };
      this.session.queueRequest("/cmd/requestSaiCalibration", {
        portNumber: this.portNumber, 
        channelNumber: this.channelNumber 
      }, () => {
        if (this.closed) return;
        this.waitForCalibration(false);
      });
    },
    waitForCalibration(didSubmitCalibration) {
      this.session.queueRequest("/cmd/waitForCalibration", { portNumber: this.portNumber }, (response) => {
        if (this.closed) return;

        if (!response) {
          clearTimeout(this.timer);
          this.timer = setTimeout(() => this.waitForCalibration(didSubmitCalibration), 500);
        }
        else {
          let usePreviousSignalValues = didSubmitCalibration ? false : shared.usePreviousSignalValues;
          this.setCalibrations(response, usePreviousSignalValues);
          if (shared.selectedSignal == null || !usePreviousSignalValues) {
            this.selectSignal(response.configuredSignal, usePreviousSignalValues);
          }
          this.requestAdcValue();
        }
      });
    },
    requestAdcValue() {
      this.session.queueRequest("/cmd/requestAdcValue", {
        portNumber: this.portNumber,
        channelNumber: this.channelNumber
      }, (response) => {
        if (this.closed) return;
        
        this.digital_12bits = response.digital_12bits;
        this.digital_13bits = response.digital_13bits;
        
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.requestAdcValue(), 1000);
      });
    }
  }
}

function formatTimestamp(timestamp) {
  if (timestamp == 0) {
    return "Default calibration";
  }
  else {
    let date = new Date(timestamp * 1000);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
  }
}

function round(value, decimalPlaces) {
  return parseFloat((parseFloat(value) || 0).toFixed(decimalPlaces));
}

function numericOR(x, y) {
  return isNaN(x) ? y : x;
}
</script>

<style scoped>
.calibration-header {
  flex-grow: 1;
  display: flex;
  align-items: center;
  gap: 30px;
}
.warning-message {
  padding: 10px;
  /* background: #ffdddd; */
  border: 1px solid red;
}
table td {
  height: 40px;
  text-align: left;
  white-space: nowrap;
}
table td.headerCell {
  text-align: left;
}
table td:first-of-type {
  width: 133px;
}
table td:not(:first-of-type) {
  padding-left: 25px;
}
table td.headerCell:not(:first-of-type) {
  padding-left: 50px;
}

.cancel {
  margin: 5px;
  margin-left: auto;
  font-size: larger;
  cursor: pointer;
}
fieldset {
  flex-grow: 1;
  border: 1px solid black;
}
legend {
  padding: 5px;
  font-weight: bold;
}
input, select {
  padding: 5px;
  border-radius: 3px;
  border: 1px solid rgb(77, 77, 77);
}
select {
  width: 250px;
  cursor: pointer;
}
.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}
.section-footer {
  margin-top: 10px;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
} 
.form-row {
  min-height: 45px;
  display: flex;
  align-items: center;
}
.form-row.sensor-model {
  
}
.form-row label {
  width: 133px;
  display: inline-block;
  /* text-align: right; */
  margin-right: 10px;
}
#app .border-btn {
  margin: 0;
  padding-left: 15px;
  padding-right: 15px;
  border-radius: 3px;
}
table {
  border-collapse: collapse;
  margin-bottom: 15px;
}
table button {
  margin-top: 0;
  margin-bottom: 0;
}
div.align-center {
  display: flex;
  align-items: center;
  justify-content: center;
}
th {
  font-weight: normal; 
  height: 45px;
}
.signal-header {
  text-transform: capitalize;
}
input {
  text-align: center;
}
td input {
  width: 175px;
}
button.button-option {
  width: 125px;
  height: 50px;
  font-size: smaller;
  border: 1px solid gray;
  border-radius: 3px;
}
button.button-option.selected {
  /* color: white; */
  font-weight: bold;
  /* background: rgba(99, 177, 255, 0.404); */
}
</style>

